import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Link, useSearchParams } from "react-router";
import { Building2, Check, ExternalLink, Linkedin, Send, UserRound, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { EmptyState } from "../misc/EmptyState";

// LDF: LinkedIn-Vernetzung als Arbeitsliste. Nichts wird automatisch auf LinkedIn ausgeführt (Nutzungsbedingungen):
// Katharina öffnet Profil bzw. Seite im eigenen, angemeldeten Browser, klickt dort selbst und hakt hier ab.

type Tab = "personen" | "suchen" | "firmen";
type LeadScore = "hot" | "warm" | "cold" | "skip" | null;

type CompanyRef = {
  id: number;
  name: string;
  sector?: string | null;
  lead_score: LeadScore;
  contact_blocked: boolean | null;
  hidden: boolean | null;
};

type PersonRow = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  academic_title: string | null;
  title: string | null;
  linkedin_url: string | null;
  linkedin_status: "angefragt" | "vernetzt" | "ignoriert" | null;
  linkedin_status_at: string | null;
  company_id: number | null;
  company: CompanyRef | null;
};

type CompanyRow = CompanyRef & {
  sector: string | null;
  linkedin_url: string;
  linkedin_status: "gefolgt" | "ignoriert" | null;
  linkedin_status_at: string | null;
  logo: { src?: string } | null;
};

const PROFILE_RE = /^https?:\/\/(?:[a-z]{2,3}\.)?linkedin\.com\/in\/[A-Za-z0-9\-_%]+\/?/i;
const SCORE_ORDER: Record<string, number> = { hot: 0, warm: 1, cold: 2 };
const scoreRank = (s: LeadScore | undefined, hasDeal: boolean) => (hasDeal ? -1 : (SCORE_ORDER[s ?? ""] ?? 3));

const SCORE_BADGE: Record<string, string> = {
  hot: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200",
  warm: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
  cold: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200",
};

const fullName = (p: PersonRow) => [p.academic_title, p.first_name, p.last_name].filter(Boolean).join(" ");
const cleanCompany = (name?: string) => name?.replace(/\b(GmbH|AG|UG|KG|LLP|e\.V\.|Botschaft der|Botschaft von)\b/g, "").replace(/\s+/g, " ").trim();
const asHref = (url: string) => (url.startsWith("http") ? url : `https://${url}`);
const dateDe = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "");

export const LinkedInPage = () => {
  const [params, setParams] = useSearchParams();
  const tab = (["personen", "suchen", "firmen"].includes(params.get("tab") ?? "") ? params.get("tab") : "personen") as Tab;
  const done = params.get("erledigt") === "1";
  const setTab = (t: Tab) => setParams({ tab: t });
  const toggleDone = () => setParams({ tab, ...(done ? {} : { erledigt: "1" }) });
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const notify = useNotify();

  const { data, isPending } = useQuery({
    queryKey: ["linkedin-page"],
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const companyRef = "company:companies(id,name,sector,lead_score,contact_blocked,hidden)";
      const personFields = `id,first_name,last_name,academic_title,title,linkedin_url,linkedin_status,linkedin_status_at,company_id,${companyRef}`;
      const [withProfile, companies, deals, leads] = await Promise.all([
        supabase.from("contacts").select(personFields).not("linkedin_url", "is", null).neq("linkedin_url", ""),
        supabase
          .from("companies")
          .select("id,name,sector,lead_score,contact_blocked,hidden,linkedin_url,linkedin_status,linkedin_status_at,logo")
          .not("linkedin_url", "is", null)
          .neq("linkedin_url", "")
          .order("name"),
        supabase.from("deals").select("company_id"),
        supabase.from("companies").select("id").in("lead_score", ["hot", "warm"]),
      ]);
      if (withProfile.error) throw withProfile.error;
      if (companies.error) throw companies.error;
      const dealCompanies = new Set((deals.data ?? []).map((d: { company_id: number }) => d.company_id));
      // Nur Kontakte wichtiger Firmen nachladen (sonst greift das 1.000-Zeilen-Limit der API)
      const important = [...new Set([...dealCompanies, ...(leads.data ?? []).map((c: { id: number }) => c.id)])].filter(Boolean);
      const withoutProfile = important.length
        ? await supabase.from("contacts").select(personFields).in("company_id", important).or("linkedin_url.is.null,linkedin_url.eq.")
        : { data: [], error: null };
      if (withoutProfile.error) throw withoutProfile.error;
      return {
        people: [...(withProfile.data ?? []), ...(withoutProfile.data ?? [])] as unknown as PersonRow[],
        companies: (companies.data ?? []) as CompanyRow[],
        dealCompanies,
      };
    },
  });

  useEffect(() => setSearch(""), [tab]);

  const reload = () => queryClient.invalidateQueries({ queryKey: ["linkedin-page"] });

  const setStatus = async (table: "contacts" | "companies", id: number, status: string | null, extra: Record<string, unknown> = {}) => {
    const { error } = await getSupabaseClient()
      .from(table)
      .update({ linkedin_status: status, linkedin_status_at: status ? new Date().toISOString() : null, ...extra })
      .eq("id", id);
    if (error) return notify(`Speichern fehlgeschlagen: ${error.message}`, { type: "error" });
    reload();
  };

  const term = search.trim().toLowerCase();
  const matches = (...values: (string | null | undefined)[]) => !term || values.some((v) => v?.toLowerCase().includes(term));
  // Gesperrte, ausgeblendete und Privatkunden (kein Folgegeschäft) nicht vernetzen
  const usable = (c: CompanyRef | null) => !c?.contact_blocked && !c?.hidden && !["privatkunde", "team-intern", "privates-umfeld", "rechnungsstelle"].includes(c?.sector ?? "");

  const lists = useMemo(() => {
    if (!data) return { personen: [], suchen: [], firmen: [], counts: { personen: 0, suchen: 0, firmen: 0 } };
    const deal = (id: number | null) => id != null && data.dealCompanies.has(id);
    const byPriority = <T,>(rank: (x: T) => number, name: (x: T) => string) => (a: T, b: T) => rank(a) - rank(b) || name(a).localeCompare(name(b));

    const withProfile = data.people.filter((p) => p.linkedin_url && usable(p.company));
    const openPeople = withProfile.filter((p) => p.linkedin_status !== "vernetzt" && p.linkedin_status !== "ignoriert");
    // Profil suchen: Kontakte bei Akquise-Firmen (hot/warm) oder Kunden mit Auftrag, noch ohne Profil
    const toSearch = data.people.filter(
      (p) => !p.linkedin_url && p.company && usable(p.company) && (deal(p.company_id) || p.company.lead_score === "hot" || p.company.lead_score === "warm"),
    );
    const firms = data.companies.filter((c) => usable(c));
    const openFirms = firms.filter((c) => !c.linkedin_status);

    const personRank = (p: PersonRow) => scoreRank(p.company?.lead_score, deal(p.company_id));
    const personName = (p: PersonRow) => `${p.company?.name ?? ""} ${p.last_name ?? ""}`;
    return {
      personen: (done ? withProfile.filter((p) => !openPeople.includes(p)) : openPeople)
        .filter((p) => matches(fullName(p), p.company?.name, p.title))
        .sort(byPriority(personRank, personName)),
      suchen: toSearch.filter((p) => matches(fullName(p), p.company?.name, p.title)).sort(byPriority(personRank, personName)),
      firmen: (done ? firms.filter((c) => c.linkedin_status) : openFirms)
        .filter((c) => matches(c.name))
        .sort(byPriority((c) => scoreRank(c.lead_score, deal(c.id)), (c) => c.name)),
      counts: { personen: openPeople.length, suchen: toSearch.length, firmen: openFirms.length },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, done, term]);

  const TABS: [Tab, string, number][] = [
    ["personen", "Personen vernetzen", lists.counts.personen],
    ["suchen", "Profile suchen", lists.counts.suchen],
    ["firmen", "Firmen folgen", lists.counts.firmen],
  ];

  const rows = lists[tab];

  // Folge-Modus: eine Firma nach der anderen – öffnen, auf LinkedIn folgen, hier abhaken, nächste öffnet sich
  const followStats = useMemo(() => {
    const firms = (data?.companies ?? []).filter((c) => usable(c));
    const today = new Date().toISOString().slice(0, 10);
    return {
      total: firms.length,
      followed: firms.filter((c) => c.linkedin_status === "gefolgt").length,
      today: firms.filter((c) => c.linkedin_status === "gefolgt" && (c.linkedin_status_at ?? "").slice(0, 10) === today).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const queue = tab === "firmen" && !done && !term ? (lists.firmen as CompanyRow[]) : [];
  const [skipped, setSkipped] = useState<number[]>([]);
  // welche Firmenseiten in dieser Sitzung schon geöffnet wurden (nur die erste muss man selbst öffnen)
  const [opened, setOpened] = useState<number[]>([]);
  const current = queue.find((c) => !skipped.includes(c.id)) ?? null;
  const nextAfter = (id: number) => queue.find((c) => c.id !== id && !skipped.includes(c.id)) ?? null;
  const openCompany = (c: CompanyRow | null) => {
    if (!c?.linkedin_url) return;
    window.open(asHref(c.linkedin_url), "_blank", "noopener");
    setOpened((l) => [...l, c.id]);
  };
  const followedAndNext = async (c: CompanyRow) => {
    const next = nextAfter(c.id);
    // nächste Seite noch im Tipp öffnen (sonst blockiert der Browser das neue Fenster)
    openCompany(next);
    await setStatus("companies", c.id, "gefolgt");
  };

  return (
    <div className="mt-4 max-w-[1800px] mx-auto">
      <Card>
        <CardContent className="space-y-4">
          <div>
            <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
              <Linkedin className="h-5 w-5" /> LinkedIn
            </h1>
            <p className="text-sm text-muted-foreground">
              Arbeitsliste zum Vernetzen, wichtigste zuerst: Kunden mit Auftrag, dann Akquise hot und warm. „Öffnen“ zeigt Profil oder Seite auf
              LinkedIn im neuen Tab; dort selbst auf „Vernetzen“ bzw. „Folgen“ klicken und hier abhaken. Das CRM greift nie selbst auf LinkedIn zu.
              Gesperrte und ausgeblendete Firmen erscheinen nicht.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-full bg-muted p-1">
              {TABS.map(([value, label, count]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTab(value)}
                  className={`rounded-full px-3 py-1 text-sm cursor-pointer ${tab === value ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                >
                  {label} <span className="text-xs text-muted-foreground">{count}</span>
                </button>
              ))}
            </div>
            {tab !== "suchen" && (
              <button
                type="button"
                onClick={toggleDone}
                className={`h-9 rounded-full px-3 text-sm cursor-pointer ${done ? "bg-secondary text-secondary-foreground" : "border border-border text-muted-foreground hover:bg-muted"}`}
              >
                {done ? "Erledigte werden gezeigt" : "Erledigte zeigen"}
              </button>
            )}
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen: Name, Firma …"
              className="h-9 min-w-48 flex-1 rounded-md border border-input bg-background px-3 text-sm"
            />
          </div>

          {tab === "firmen" && !done && !isPending && (
            <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>
                  <strong>{followStats.followed}</strong> von {followStats.total} Firmen gefolgt · heute <strong>{followStats.today}</strong>
                </span>
                <span className={`text-xs ${followStats.today >= 40 ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}>
                  {followStats.today >= 40 ? "Für heute reicht es – morgen weiter (wirkt für LinkedIn natürlicher)" : "Tipp: 40–50 pro Tag"}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-border">
                <div className="h-full bg-primary" style={{ width: `${followStats.total ? (followStats.followed / followStats.total) * 100 : 0}%` }} />
              </div>
              {current ? (
                <div className="flex flex-wrap items-center gap-3">
                  {current.logo?.src ? (
                    <img src={current.logo.src} alt="" className="h-12 w-12 shrink-0 rounded bg-background object-contain" />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-muted">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-muted-foreground">Als Nächstes</div>
                    <div className="truncate font-medium">{current.name}</div>
                    <Badges score={current.lead_score} deal={data!.dealCompanies.has(current.id)} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {opened.includes(current.id) ? (
                      <>
                        <Button onClick={() => followedAndNext(current)} className="gap-1 bg-[#0A66C2] text-white hover:bg-[#004182]">
                          <Check className="h-4 w-4" /> Gefolgt – nächste öffnen
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openCompany(current)} title="Falls der Tab geschlossen wurde">
                          Seite erneut öffnen
                        </Button>
                      </>
                    ) : (
                      <Button onClick={() => openCompany(current)} className="gap-1 bg-[#0A66C2] text-white hover:bg-[#004182]">
                        <Linkedin className="h-4 w-4" /> Los geht’s – Seite öffnen
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setSkipped((l) => [...l, current.id])} title="Später">
                      Überspringen
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setStatus("companies", current.id, "ignoriert")} title="Nicht folgen, aus der Liste nehmen">
                      <X className="h-3.5 w-3.5" /> Nicht folgen
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{skipped.length ? "Nur noch übersprungene Firmen übrig." : "Allen Firmen gefolgt 🎉"}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Ablauf: einmal „Seite öffnen“ – danach immer nur: auf LinkedIn <strong>+ Folgen</strong> tippen, Tab schließen, hier <strong>„Gefolgt – nächste öffnen“</strong>.
              </p>
            </div>
          )}

          {isPending ? (
            <p className="py-6 text-sm text-muted-foreground">Wird geladen …</p>
          ) : rows.length === 0 ? (
            <EmptyState
              Icon={Linkedin}
              title={tab === "firmen" && !done ? "Allen Firmen gefolgt" : tab === "personen" && !done ? "Keine offenen Profile" : tab === "suchen" ? "Nichts zu suchen" : "Nichts in dieser Liste"}
              text={
                tab === "personen" && !done
                  ? "Neue kommen aus E-Mail-Signaturen oder über „Profile suchen“."
                  : tab === "suchen"
                    ? "Alle wichtigen Kontakte haben ein Profil oder sind noch nicht angelegt."
                    : undefined
              }
            />
          ) : (
            <ul className="divide-y divide-border">
              {tab === "firmen"
                ? (rows as CompanyRow[]).map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center gap-3 py-2">
                      {c.logo?.src ? (
                        <img src={c.logo.src} alt="" className="h-8 w-8 shrink-0 rounded object-contain bg-background" />
                      ) : (
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-muted">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <Link to={`/companies/${c.id}/show`} className="text-sm font-medium">
                          {c.name}
                        </Link>
                        <Badges score={c.lead_score} deal={data!.dealCompanies.has(c.id)} />
                        {c.linkedin_status && (
                          <div className="text-xs text-muted-foreground">
                            {c.linkedin_status === "gefolgt" ? "Gefolgt" : "Nicht folgen"} am {dateDe(c.linkedin_status_at)}
                          </div>
                        )}
                      </div>
                      <OpenLink href={asHref(c.linkedin_url)} label="Seite öffnen" />
                      {c.linkedin_status ? (
                        <Button variant="ghost" size="sm" onClick={() => setStatus("companies", c.id, null)}>
                          Zurücksetzen
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" className="gap-1" onClick={() => setStatus("companies", c.id, "gefolgt")}>
                            <Check className="h-3.5 w-3.5" /> Gefolgt
                          </Button>
                          <Button variant="ghost" size="sm" title="Nicht folgen, aus der Liste nehmen" onClick={() => setStatus("companies", c.id, "ignoriert")}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </li>
                  ))
                : (rows as PersonRow[]).map((p) =>
                    tab === "suchen" ? (
                      <SearchRow key={p.id} person={p} deal={data!.dealCompanies.has(p.company_id ?? -1)} onSaved={reload} />
                    ) : (
                      <li key={p.id} className="flex flex-wrap items-center gap-3 py-2">
                        <PersonLabel person={p} deal={data!.dealCompanies.has(p.company_id ?? -1)} />
                        <OpenLink href={asHref(p.linkedin_url!)} label="Profil öffnen" />
                        {p.linkedin_status === "vernetzt" || p.linkedin_status === "ignoriert" ? (
                          <Button variant="ghost" size="sm" onClick={() => setStatus("contacts", p.id, null)}>
                            Zurücksetzen
                          </Button>
                        ) : (
                          <>
                            {p.linkedin_status !== "angefragt" && (
                              <Button variant="outline" size="sm" className="gap-1" onClick={() => setStatus("contacts", p.id, "angefragt")}>
                                <Send className="h-3.5 w-3.5" /> Angefragt
                              </Button>
                            )}
                            <Button size="sm" className="gap-1" onClick={() => setStatus("contacts", p.id, "vernetzt")}>
                              <Check className="h-3.5 w-3.5" /> Vernetzt
                            </Button>
                            <Button variant="ghost" size="sm" title="Nicht vernetzen, aus der Liste nehmen" onClick={() => setStatus("contacts", p.id, "ignoriert")}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </li>
                    ),
                  )}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

LinkedInPage.path = "/linkedin";

const Badges = ({ score, deal }: { score: LeadScore | undefined; deal: boolean }) => (
  <span className="ml-2 inline-flex gap-1 align-middle text-[11px]">
    {deal && <span className="rounded-full bg-blue-600 px-2 py-0.5 text-white">Kunde</span>}
    {score && SCORE_BADGE[score] && <span className={`rounded-full px-2 py-0.5 ${SCORE_BADGE[score]}`}>{score}</span>}
  </span>
);

const OpenLink = ({ href, label }: { href: string; label: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-sm no-underline hover:bg-muted"
  >
    <ExternalLink className="h-3.5 w-3.5" /> {label}
  </a>
);

const PersonLabel = ({ person: p, deal }: { person: PersonRow; deal: boolean }) => (
  <div className="flex min-w-0 flex-1 items-center gap-2">
    <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
    <div className="min-w-0">
      <Link to={`/contacts/${p.id}/show`} className="text-sm font-medium">
        {fullName(p) || "Ohne Namen"}
      </Link>
      <Badges score={p.company?.lead_score} deal={deal} />
      <div className="truncate text-xs text-muted-foreground">
        {[p.title, p.company?.name].filter(Boolean).join(" · ")}
        {p.linkedin_status === "angefragt" && ` · angefragt am ${dateDe(p.linkedin_status_at)}`}
        {p.linkedin_status === "vernetzt" && ` · vernetzt am ${dateDe(p.linkedin_status_at)}`}
        {p.linkedin_status === "ignoriert" && ` · nicht vernetzen (${dateDe(p.linkedin_status_at)})`}
      </div>
    </div>
  </div>
);

const SearchRow = ({ person: p, deal, onSaved }: { person: PersonRow; deal: boolean; onSaved: () => void }) => {
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const notify = useNotify();
  const keywords = [p.first_name, p.last_name, cleanCompany(p.company?.name)].filter(Boolean).join(" ");
  const searchUrl = `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(keywords)}`;

  const save = async () => {
    const match = url.trim().match(PROFILE_RE);
    if (!match) return notify("Bitte einen Profil-Link wie https://www.linkedin.com/in/… einfügen", { type: "warning" });
    setSaving(true);
    const { error } = await getSupabaseClient().from("contacts").update({ linkedin_url: match[0].replace(/\/$/, "") }).eq("id", p.id);
    setSaving(false);
    if (error) return notify(`Speichern fehlgeschlagen: ${error.message}`, { type: "error" });
    notify("Profil gespeichert – steht jetzt unter „Personen vernetzen“", { type: "success" });
    onSaved();
  };

  return (
    <li className="flex flex-wrap items-center gap-3 py-2">
      <PersonLabel person={p} deal={deal} />
      <OpenLink href={searchUrl} label="Auf LinkedIn suchen" />
      <div className="flex gap-1">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="Profil-Link einfügen"
          className="h-8 w-56 rounded-md border border-input bg-background px-2 text-xs"
        />
        <Button size="sm" className="h-8 gap-1" disabled={saving || !url.trim()} onClick={save}>
          <Check className="h-3.5 w-3.5" /> Speichern
        </Button>
      </div>
    </li>
  );
};
